import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, Building2, Briefcase, Calendar } from 'lucide-react'
import { leadsApi } from '../../lib/api'
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import type { Lead } from '../../types'

export function LeadsList() {
  const { t } = useTranslation()
  const [leads, setLeads] = useState<Lead[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const data = await leadsApi.getAll()
        setLeads(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('admin.leads.error'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchLeads()
  }, [t])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <Card variant="default" className="border-error/30">
        <p className="text-error">{error}</p>
      </Card>
    )
  }

  if (leads.length === 0) {
    return (
      <Card variant="default" className="text-center">
        <p className="text-text-secondary">{t('admin.leads.empty')}</p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">{t('admin.leads.title')}</h1>
        <Badge variant="accent">{leads.length} {t('admin.leads.count')}</Badge>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-surface-elevated">
              <tr>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-muted">{t('admin.leads.table.name')}</th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-muted">{t('admin.leads.table.contact')}</th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-muted">{t('admin.leads.table.company')}</th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-muted">{t('admin.leads.table.date')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {leads.map((lead) => (
                <tr key={lead.id} className="bg-surface hover:bg-surface-elevated">
                  <td className="px-4 py-4">
                    <div className="font-medium text-text-primary">{lead.name}</div>
                    {lead.message && (
                      <p className="mt-1 max-w-xs truncate text-sm text-text-secondary">{lead.message}</p>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Mail className="h-4 w-4" />
                      {lead.email}
                    </div>
                    {lead.role && (
                      <div className="mt-1 flex items-center gap-2 text-sm text-text-muted">
                        <Briefcase className="h-4 w-4" />
                        {lead.role}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {lead.company ? (
                      <div className="flex items-center gap-2 text-sm text-text-secondary">
                        <Building2 className="h-4 w-4" />
                        {lead.company}
                      </div>
                    ) : (
                      <span className="text-sm text-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Calendar className="h-4 w-4" />
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
